import { Amplify, type ResourcesConfig } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import { monitoringCaptureError } from "@/plugins/monitoring";

// MODE, not PROD: `vite build --mode staging` runs with NODE_ENV=production, so PROD is true on staging too.
const defaultRedirectOrigins =
	import.meta.env.MODE === "production"
		? ["https://signaloid.io"]
		: ["https://localhost:3000", "https://cpu1.signaloid.dev", "https://signaloid.io"];

const customerDomainOrigin =
	import.meta.env.MODE === "production"
		? /^https:\/\/[a-z0-9-]+\.signaloid\.io$/
		: /^https:\/\/[a-z0-9-]+\.signaloid\.dev$/;

function currentCustomerDomainOrigin(): string[] {
	const origin = typeof window === "undefined" ? "" : window.location.origin;
	return customerDomainOrigin.test(origin) && !defaultRedirectOrigins.includes(origin) ? [origin] : [];
}

const redirectOrigins = [...defaultRedirectOrigins, ...currentCustomerDomainOrigin()];

export const AwsConfig: ResourcesConfig = {
	Auth: {
		Cognito: {
			userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "",
			userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_APP_ID ?? "",
			loginWith: {
				oauth: {
					domain: import.meta.env.VITE_COGNITO_OAUTH_DOMAIN ?? "",
					scopes: ["openid", "email", "profile", "aws.cognito.signin.user.admin"],
					redirectSignIn: redirectOrigins,
					redirectSignOut: redirectOrigins.map((origin) => `${origin}/login`),
					responseType: "code",
					providers: ["Google"],
				},
			},
		},
	},
};

function cookieSecurityOptions(): { sameSite: "strict"; secure: boolean } {
	return { sameSite: "strict", secure: window.location.protocol === "https:" };
}

function createDomainCookieStorage(domain: string): CookieStorage {
	return new CookieStorage({ domain, ...cookieSecurityOptions() });
}

class AuthCookieStorage extends CookieStorage {
	public override async removeItem(key: string): Promise<void> {
		await super.removeItem(key);
		// Old-bundle tabs write a domain=<hostname> copy, which outlives the host-only removal.
		await createDomainCookieStorage(window.location.hostname).removeItem(key);
	}
}

export function createAuthCookieStorage(): CookieStorage {
	return new AuthCookieStorage({
		// Keeps session cookies. Without the key, Amplify writes 365-day cookies.
		expires: undefined,
		...cookieSecurityOptions(),
	});
}

function reportMigrationError(error: unknown): void {
	// Both entry scripts call configureAuth before Sentry.init, so a synchronous report is dropped.
	setTimeout(() => monitoringCaptureError(error, "Migrate legacy auth cookies"));
}

function readCognitoCookies(): Map<string, string> {
	const cookiePrefix: string = `CognitoIdentityServiceProvider.${import.meta.env.VITE_COGNITO_CLIENT_APP_ID}.`;
	const cognitoCookies: Map<string, string> = new Map();
	for (const cookiePair of document.cookie.split("; ")) {
		const separatorIndex: number = cookiePair.indexOf("=");
		const cookieName: string = cookiePair.slice(0, separatorIndex);
		if (cookieName.startsWith(cookiePrefix)) {
			cognitoCookies.set(decodeURIComponent(cookieName), cookiePair.slice(separatorIndex + 1));
		}
	}
	return cognitoCookies;
}

// Runs on every boot, removable once browsers no longer hold pre-migration cookies.
export function migrateLegacyAuthCookies(): void {
	const hostname: string = window.location.hostname;
	const hostnameLabels: string[] = hostname.split(".");
	const legacyCookieNames: string[] = [...readCognitoCookies().keys()];
	if (legacyCookieNames.length === 0) {
		return;
	}
	for (let labelIndex: number = 1; labelIndex < hostnameLabels.length - 1; labelIndex++) {
		const parentDomain: string = hostnameLabels.slice(labelIndex).join(".");
		const parentDomainStorage: CookieStorage = createDomainCookieStorage(parentDomain);
		for (const cookieName of legacyCookieNames) {
			void parentDomainStorage.removeItem(cookieName);
		}
	}

	// Expiring the domain copy leaves a host-only copy of the same name in place.
	const cognitoCookies: Map<string, string> = readCognitoCookies();
	const hostDomainStorage: CookieStorage = createDomainCookieStorage(hostname);
	for (const cookieName of cognitoCookies.keys()) {
		void hostDomainStorage.removeItem(cookieName);
	}
	const hostOnlyCookies: Map<string, string> = readCognitoCookies();
	const authCookieStorage: CookieStorage = createAuthCookieStorage();
	for (const [cookieName, cookieValue] of cognitoCookies) {
		if (hostOnlyCookies.has(cookieName)) {
			continue;
		}
		try {
			// setItem encodes the value again, so pass it decoded.
			void authCookieStorage.setItem(cookieName, decodeURIComponent(cookieValue));
		} catch {
			reportMigrationError(new Error(`Dropped undecodable legacy Cognito ${cookieName.split(".").pop()} cookie`));
		}
	}
}

export function configureAuth(): void {
	try {
		migrateLegacyAuthCookies();
	} catch (error) {
		reportMigrationError(error instanceof Error ? error : new Error(String(error)));
	}
	Amplify.configure(AwsConfig);
	// Amplify pins the token provider's auth config on the first configure() call
	// only. Assert ours, or token refresh uses whichever pool got there first.
	if (AwsConfig.Auth) {
		cognitoUserPoolsTokenProvider.setAuthConfig(AwsConfig.Auth);
	}
	cognitoUserPoolsTokenProvider.setKeyValueStorage(createAuthCookieStorage());
}
